import { PropsWithChildren, useMemo } from 'react';
import BigNumber from 'bignumber.js';
import createSafeContext from '@paradigm/utils/createSafeContext';

import { getOppositeSide } from '#/unified-rfqs/ui/rfqs/quotes-functions';
import { useGrfqBbo } from '#/unified-rfqs/ui/rfqs/bbo';
import {
  isValidDecimal,
  parseFormattedFloat,
} from '@paradigm/utils/formatDecimal';

export interface RfqMarkPrice {
  readonly markPrice: string | null;
  // Basis-agnostic: compares whatever `price` it is given against
  // `markPrice`. Both the mark and order/quote prices are served net of
  // the hedge, so a hedged RFQ compares net against net.
  readonly isMarkOrBetter: (
    price: string | undefined,
    side: 'BUY' | 'SELL',
  ) => boolean;
}

export const [useRfqMarkPrice, RfqMarkPriceValueProvider] =
  createSafeContext<RfqMarkPrice>('RfqMarkPrice');

export function RfqMarkPriceProvider({ children }: PropsWithChildren) {
  const bboResp = useGrfqBbo();
  const markPrice = bboResp.data?.mark_price ?? null;

  const value: RfqMarkPrice = useMemo(
    () => ({
      markPrice,
      isMarkOrBetter: (price, side) => isMarkOrBetter(markPrice, price, side),
    }),
    [markPrice],
  );

  return (
    <RfqMarkPriceValueProvider value={value}>
      {children}
    </RfqMarkPriceValueProvider>
  );
}

export function isMarkOrBetter(
  markPrice: string | null,
  price: string | undefined | null,
  buttonSide: 'BUY' | 'SELL',
): boolean {
  const aggressSide = getOppositeSide(buttonSide);

  if (price == null || markPrice == null) return false;
  if (!isValidDecimal(price) || !isValidDecimal(markPrice)) return false;

  const priceBigNum = BigNumber(parseFormattedFloat(price));
  const markPriceBigNum = BigNumber(parseFormattedFloat(markPrice));

  if (aggressSide === 'BUY') {
    return priceBigNum.lte(markPriceBigNum);
  }

  return priceBigNum.gte(markPriceBigNum);
}
